import { useRef, useState } from 'react';
import moment from 'moment';
import i18n from 'helpers/i18n';
import messages from './messages';
import PageLoader from 'components/PageLoader';
import useCurrentUser from 'hooks/useCurrentUser';
import { composeHierarchy } from './utils';
import { roleToText } from 'helpers/role';
import { useGetClientAccountRoles } from 'actions/roles';
import { useParams, useNavigate } from 'react-router-dom';
import { ActivitySection } from './ActivitySection';
import { SubscriptionsTable } from './Subscriptions';
import { CertificationsTable } from './Certifications';
import { TrainingsTable } from './Trainings';
import { useQueryClient } from '@tanstack/react-query';
import { AccountMember } from 'types/api/AccountMember';
import { Input } from 'components/Organization/Input';
import { IconButton } from 'components/Organization/IconButton';
import { PrimaryButton } from 'components/Organization/PrimaryButton';
import { Select, SelectOption } from 'components/Organization/Select';
import { EditableField } from 'components/Organization/EditableField';
import { EditableFieldsGroup } from 'components/Organization/EditableFieldsGroup';
import { updateAccountMember, useGetAccountMember } from 'actions/accounts';
import useTimestamp from 'hooks/useTimestamp';

import {
  HierarchyInput,
  PrimaryAdminHierarchyDisplay,
  useAccountHierarchy
} from 'components/Organization/Hierarchy';
import useIsPrimaryAdmin from '../hooks/useIsPrimaryAdmin';
import { inactiveRolesFilter } from 'helpers/role/utils';

interface ViewMemberProps {
  /** Current account ID. */
  accountId: number;

  /** Account member data. */
  user: AccountMember;

  /** List of roles formatted for select component. */
  roles: SelectOption[];
}

function ViewMember({ accountId, user, roles }: ViewMemberProps) {
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const hierarchy = useAccountHierarchy(accountId);
  const [timestamp, refreshTimestamp] = useTimestamp();
  const navigate = useNavigate();
  const [error, setError] = useState<string>('');
  const prevPendingRoleRef = useRef<string | number | undefined>(user.role_id);
  const isPrimaryAdmin = useIsPrimaryAdmin(roles);

  const handleFieldSave = async (field: string | string[], value: any) => {
    try {
      const payload = Array.isArray(field)
        ? field.reduce(
            (acc, field, idx) => ({ ...acc, [field]: value[idx] }),
            {}
          )
        : typeof value === 'object'
          ? value
          : { [field]: value };
      const response = await updateAccountMember(accountId, user.user_id, {
        account_user: payload
      });
      queryClient.setQueryData(
        ['ca', 'accounts', accountId, 'users', user.user_id],
        response.data
      );
      refreshTimestamp();
      setError('');
    } catch (error: any) {
      const errorsObj = error?.response?.data?.errors;
      let errorMessages: string[] = [];
      if (errorsObj && typeof errorsObj === 'object') {
        for (const key in errorsObj) {
          if (Array.isArray(errorsObj[key])) {
            errorMessages = errorMessages.concat(errorsObj[key]);
          }
        }
      }
      if (errorMessages.length === 0) {
        const fallback = 'An error occurred while saving.';
        errorMessages = [fallback];
      }
      setError(errorMessages.join(', '));
      throw error;
    }
  };

  const roleIsPrimaryAdmin = user.role === 'ca_admin';
  const primaryAdminRoleId = roles.find(role =>
    /primary admin/i.test(role.text)
  )?.value;
  const allowedToEdit = currentUser.current_account_id === user.account_id;
  // Users cannot edit their own role or hierarchy unless they're a Primary Admin
  const allowedToEditRoleAndHierarchy =
    currentUser.id !== user.user_id || roleIsPrimaryAdmin;
  const adminCanEditAccountRole = user?.all_target_nodes_visible;

  return (
    <div className="px-4 md:px-6 xl:px-10 max-w-7xl mx-auto text-black">
      <div className="font-sans pt-12 pb-4 flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
        <div className="flex items-center gap-2">
          <IconButton
            label={i18n.ft(messages.goBack)}
            icon="fa-solid fa-arrow-left"
            onClick={() => navigate(-1)}
          />

          <h2 className="font-sans text-2xl sm:text-3xl">{user.user}</h2>
        </div>

        {user.last_login && (
          <div>
            <span className="font-semibold">
              {i18n.ft(messages.lastLogin) + ': '}
            </span>
            <span>{moment(user.last_login).format('MM/DD/YYYY')}</span>
          </div>
        )}
      </div>

      <div className="font-sans bg-white p-6 rounded-2xl">
        <div className="font-bold text-xl md:text-2xl">
          {i18n.ft(messages.basicInformation)}
        </div>

        <div className="mt-4 grid sm:grid-cols-[_1fr_auto_1fr]">
          <div>
            <EditableField
              border={false}
              label={i18n.ft(messages.email)}
              value={user.email}
              disabled={!allowedToEdit}
            />

            <EditableField
              label={i18n.ft(messages.addedOn)}
              value={moment(user.added_on).format('MM/DD/YYYY')}
            />

            <EditableField
              label={i18n.ft(messages.addedBy)}
              value={user.added_by || i18n.ft(messages.unknown)}
            />
          </div>

          <div className="hidden sm:block my-6 mx-8 border-l border-[#D7DFE8]" />
          <div>
            <EditableField
              value={user.external_id}
              label={i18n.ft(messages.externalId)}
              initialValue={user.external_id ?? ''}
              onSave={value => handleFieldSave('external_id', value)}
              disabled={!allowedToEdit}
              className="border-none"
              control={({ value, setValue }) => (
                <Input
                  value={value}
                  label={i18n.ft(messages.externalId)}
                  onChange={event => setValue(event.target.value)}
                />
              )}
            />

            {user.hierarchy_present ? (
              <EditableFieldsGroup
                disabled={!allowedToEditRoleAndHierarchy}
                error={error}
                onCancel={() => setError('')}
                onSave={async values => {
                  const [roleId, nodeIds] = values as [string, number[]];

                  await handleFieldSave('role_id,node_ids', {
                    role_id: roleId,
                    node_ids: nodeIds
                  });
                }}
                fields={[
                  {
                    label: i18n.ft(messages.role),
                    value: roleToText(user.role),
                    initialValue: user.role_id,
                    control: ({
                      value,
                      setValue
                    }: {
                      value: string | number;
                      setValue: (v: string | number) => void;
                    }) => (
                      <>
                        <Select
                          value={value}
                          options={roles}
                          onChange={setValue}
                          label={i18n.ft(messages.role)}
                          placeholder={i18n.ft(messages.rolePlaceholder)}
                          disabled={!adminCanEditAccountRole}
                        />
                        {!adminCanEditAccountRole && (
                          <div className="mt-1 font-semibold text-sm text-gray-600 m-0 px-1 flex items-center gap-1">
                            <i
                              className="fa-solid fa-circle-info self-start mt-1"
                              aria-hidden="true"
                            />
                            {i18n.ft(messages.adminAccountRoleInfo)}
                          </div>
                        )}
                      </>
                    )
                  },
                  {
                    label: i18n.ft(messages.hierarchy),
                    value: composeHierarchy(user.hierarchy),
                    initialValue: user.node_ids,
                    control: ({ value, setValue, internalValues }) => {
                      const pendingRole = internalValues?.[0];
                      const rootNodeId = hierarchy.nodes?.[0]?.id;
                      const rootNodeName = hierarchy.nodes?.[0]?.name;
                      // If switching to primary admin, set node_ids to root node id
                      if (pendingRole === primaryAdminRoleId) {
                        if (
                          rootNodeId &&
                          (!value ||
                            value.length !== 1 ||
                            value[0] !== rootNodeId)
                        ) {
                          setValue([rootNodeId]);
                        }
                        prevPendingRoleRef.current = pendingRole;
                        return (
                          <PrimaryAdminHierarchyDisplay name={rootNodeName} />
                        );
                      }
                      // If switching from primary admin to non-primary admin, clear node_ids
                      if (
                        prevPendingRoleRef.current === primaryAdminRoleId &&
                        pendingRole !== primaryAdminRoleId
                      ) {
                        if (value && value.length > 0) {
                          setValue([]);
                        }
                      }
                      prevPendingRoleRef.current = pendingRole;
                      return (
                        <HierarchyInput
                          {...hierarchy}
                          value={value}
                          onChange={setValue}
                          label={i18n.ft(messages.hierarchy)}
                          placeholder={i18n.ft(messages.hierarchyPlaceholder)}
                        />
                      );
                    }
                  }
                ]}
              />
            ) : (
              <EditableField
                label={i18n.ft(messages.role)}
                value={roleToText(user.role)}
                initialValue={user.role_id}
                onSave={value => handleFieldSave('role_id', value)}
                disabled={
                  !allowedToEditRoleAndHierarchy || !adminCanEditAccountRole
                }
                control={({ value, setValue }) => (
                  <Select
                    value={value}
                    options={roles}
                    onChange={setValue}
                    label={i18n.ft(messages.role)}
                    placeholder={i18n.ft(messages.rolePlaceholder)}
                  />
                )}
              />
            )}
          </div>
        </div>
      </div>

      <SubscriptionsTable userId={user.user_id} />
      <CertificationsTable userId={user.user_id} />
      <TrainingsTable userId={user.user_id} />
      {isPrimaryAdmin && (
        <ActivitySection key={timestamp} accountId={accountId} user={user} />
      )}
    </div>
  );
}

function ViewMemberController() {
  const params = useParams();
  const navigate = useNavigate();
  const memberId = Number(params.memberId);
  const currentUser = useCurrentUser();
  const accountId = currentUser.current_account_id ?? 0;

  const { data: memberData, isSuccess: memberIsSuccess } = useGetAccountMember(
    accountId,
    memberId
  );

  const { data, isSuccess: rolesIsSuccess } =
    useGetClientAccountRoles(accountId);

  if (!memberIsSuccess || !rolesIsSuccess) {
    return <PageLoader />;
  }

  const roles = data
    .filter(a => inactiveRolesFilter(a))
    .map(role => ({
      value: role.id,
      text: roleToText(role.name)
    }));

  const canViewMember = memberData?.is_visible_to_current_user;

  if (canViewMember) {
    return <ViewMember accountId={accountId} user={memberData} roles={roles} />;
  }

  return (
    <div className="px-4 md:px-6 xl:px-10 max-w-7xl mx-auto text-black">
      <div className="font-sans pt-12 pb-4 flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
        <div className="flex items-center gap-2">
          <IconButton
            label={i18n.ft(messages.goBack)}
            icon="fa-solid fa-arrow-left"
            onClick={() => navigate(-1)}
          />
          <h2 className="font-sans text-2xl sm:text-3xl">
            {i18n.ft(messages.accessRequired)}
          </h2>
        </div>
      </div>

      <div className="font-sans bg-white p-6 rounded-2xl">
        <p className="text-base text-black">
          {i18n.ft(messages.accessRequiredDescription)}
        </p>
        <div className="mt-6">
          <PrimaryButton
            icon="fa-solid fa-plus"
            onClick={() => navigate('/organization/add-members')}
          >
            {i18n.ft(messages.addMember)}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

export default ViewMemberController;
